import { Component, ElementRef, OnInit, QueryList, ViewChildren } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { forkJoin } from 'rxjs';
import { take } from 'rxjs/operators';
import { baseDayReportFields } from '../../../../environments/trackline/entitytypes';
import { ArticleDayreport } from '../../../classes/article-dayreport.class';
import { Dayreport } from '../../../classes/dayreport.class';
import { MaterialType } from '../../../classes/material-type.class';
import { Material } from '../../../classes/material.class';
import { Planning } from '../../../classes/planning.class';
import { RawMaterial } from '../../../classes/raw-material.class';
import { Realisation } from '../../../classes/realisation';
import { CodaltComponent } from '../../../codalt.component';
import { DayreportService } from '../../../services/dayreport.service';
import { EntityField, EntityId, EntityTypeCode, FieldType } from '../../../services/entities/entity-type.class';
import { MaterialService } from '../../../services/material/material.service';
import { PlanningService } from '../../../services/planning/planning.service';
import { RealisationService } from '../../../services/realisation.service';
import { Utils } from '../../../utils.class';

interface PrintFieldRow {
    label: string;
    value1: string;
    value2?: string;
}

interface PrintFieldGroup {
    key: string;
    label: string;
    paired: boolean;
    rows: PrintFieldRow[];
}

interface PrintSpecificationRow {
    id: number;
    location: string;
    amount: number;
    unit: string | undefined;
    marking: string;
    m2: number | null;
    material: Material | null;
}

interface PrintProductRow {
    key: string;
    name: string;
    color: string;
    supplier: string | undefined;
    certificate: string | undefined;
    consumption: number;
    consumptionUnit: string | undefined;
    m2: number;
    kg: number;
}

interface PrintHourRow {
    begindate: Date;
    enddate: Date;
    hourtype: Realisation['hourtype'];
    pause: number | undefined;
    comments: string[];
}

interface PrintHourGroup {
    key: string;
    label: string;
    names: string[];
    rows: PrintHourRow[];
}

@Component({
    selector: 'app-trackline-day-report-print',
    standalone: false,
    templateUrl: './trackline-day-report-print.component.html',
    styleUrl: './trackline-day-report-print.component.scss',
})
export class TracklineDayReportPrintComponent extends CodaltComponent implements OnInit {
    @ViewChildren('pageContent') pageContents: QueryList<ElementRef<HTMLElement>>;

    planning: Planning;
    dayReport: Dayreport | null = null;
    hourGroups: PrintHourGroup[] = [];

    employees: string[] = [];
    equipment: string[] = [];
    groups: PrintFieldGroup[] = [];
    specificationRows: PrintSpecificationRow[] = [];
    specificationTotalM2 = 0;
    productRows: PrintProductRow[] = [];
    productTotalM2 = 0;
    productTotalKg = 0;

    pagesPerSection = [1, 1, 1];

    readonly landscapeSections = [1];

    window = window;

    constructor(
        private route: ActivatedRoute,
        private planningService: PlanningService,
        private dayreportService: DayreportService,
        private realisationService: RealisationService,
        private materialService: MaterialService
    ) {
        super();
    }

    ngOnInit(): void {
        const planningId = +this.route.snapshot.params.planningId;

        this.subscriptions.add(
            forkJoin({
                planning: this.planningService.getSingle(planningId).pipe(take(1)),
                dayReport: this.dayreportService.getByPlanningId(planningId).pipe(take(1)),
                realisations: this.realisationService.getPlanningRealisations(planningId).pipe(take(1)),
                materials: this.materialService.getList(true).pipe(take(1)),
                materialTypes: this.materialService.getMaterialTypes().pipe(take(1)),
                rawMaterials: this.materialService.getAllRawMaterials().pipe(take(1)),
            }).subscribe(({ planning, dayReport, realisations, materials, materialTypes, rawMaterials }) => {
                this.planning = planning;
                this.dayReport = dayReport.data ?? null;
                this.hourGroups = this.buildHourGroups(
                    (realisations.data ?? []).filter(realisation => !realisation.removed && !realisation.deleted_at)
                );

                this.employees = this.buildEmployees(planning);
                this.equipment = this.buildEquipment(planning);
                this.groups = this.buildGroups(this.dayReport);
                this.buildProduction(this.dayReport, materials, materialTypes, rawMaterials);

                setTimeout(() => {
                    this.measurePages();
                    this.print();
                });
            })
        );
    }

    print() {
        if (this.route.snapshot.queryParams.print === 'true') {
            setTimeout(() => {
                window.print();
                window.close();
            });
        }
    }

    pageHeightMm(section: number): number {
        return this.landscapeSections.includes(section) ? 210 : 297;
    }

    pageFooters(section: number): { label: string; topMm: number }[] {
        const total = this.pagesPerSection.reduce((sum, pages) => sum + pages, 0);
        const start = this.pagesPerSection.slice(0, section).reduce((sum, pages) => sum + pages, 1);
        const height = this.pageHeightMm(section);

        return Array.from({ length: this.pagesPerSection[section] }, (_, page) => ({
            label: `Pagina ${start + page} van ${total}`,
            topMm: page * height + height - 12 - 1,
        }));
    }

    private measurePages(): void {
        const mmToPx = 96 / 25.4;

        this.pagesPerSection = this.pagesPerSection.map((_, section) => {
            const content = this.pageContents.find(c => c.nativeElement.dataset.section === `${section}`);
            const usableHeightMm = this.pageHeightMm(section) - 24 - 15;

            return content ? Math.max(1, Math.ceil(content.nativeElement.offsetHeight / (usableHeightMm * mmToPx))) : 0;
        });
    }

    get hasSpecification(): boolean {
        return this.planning?.mainPlanning?.entitytype_key === 'markings';
    }

    private buildHourGroups(realisations: Realisation[]): PrintHourGroup[] {
        const perName = new Map<string, Realisation[]>();

        realisations.forEach(realisation => {
            const name = this.realisationName(realisation);
            perName.set(name, [...(perName.get(name) ?? []), realisation]);
        });

        const groups = new Map<string, PrintHourGroup>();

        Array.from(perName.keys())
            .sort((a, b) => a.localeCompare(b))
            .forEach(name => {
                const rows = perName
                    .get(name)!
                    .sort((a, b) => new Date(a.begindate).getTime() - new Date(b.begindate).getTime());
                const isEquipment = rows.every(row => !row.user_id && row.entity_id);
                const key =
                    (isEquipment ? 'materieel|' : 'medewerkers|') +
                    rows
                        .map(
                            row =>
                                `${new Date(row.begindate).getTime()}-${new Date(row.enddate).getTime()}-${row.hourtype}-${row.pause ?? 0}`
                        )
                        .join('|');

                const group = groups.get(key) ?? {
                    key,
                    label: isEquipment ? 'Materieel' : 'Medewerkers',
                    names: [],
                    rows: rows.map(row => ({
                        begindate: row.begindate,
                        enddate: row.enddate,
                        hourtype: row.hourtype,
                        pause: row.pause,
                        comments: [],
                    })),
                };

                group.names.push(name);
                rows.forEach((row, index) => {
                    const comment = row.comment_user?.trim();
                    if (comment && !group.rows[index].comments.includes(comment)) {
                        group.rows[index].comments.push(comment);
                    }
                });
                groups.set(key, group);
            });

        return Array.from(groups.values());
    }

    realisationName(realisation: Realisation): string {
        return (
            realisation.user?.name ??
            realisation.hiring_name ??
            realisation.planning_has?.name ??
            realisation.entity?.name ??
            ''
        );
    }

    private buildEmployees(planning: Planning): string[] {
        return [...(planning.user_planning ?? []), ...(planning.planning_has ?? [])]
            .map(employee => {
                let name = 'entitytype_key' in employee ? employee.driver_user?.name : employee.user?.name;
                if ('entitytype_key' in employee && employee.entitytype_key === EntityTypeCode.InhuurPersoneel) {
                    name = employee.name;
                }
                const begin = employee.begindate ? Utils.formatDate(employee.begindate, 'HH:mm') : '';
                const end = employee.enddate ? Utils.formatDate(employee.enddate, 'HH:mm') : '';
                const time = begin ? ` (${[begin, end].filter(Boolean).join('-')})` : '';

                return name ? `${name}${time}` : '';
            })
            .filter(Boolean);
    }

    private buildEquipment(planning: Planning): string[] {
        return (planning.planning_has ?? [])
            .filter(
                ph =>
                    ph.entity_id &&
                    ph.id !== planning.planning_has_id &&
                    ph.entity_id !== EntityId.HiringEmployee &&
                    ph.entitytype_key !== EntityTypeCode.InhuurPersoneel
            )
            .map(
                ph =>
                    `${ph.entity?.name ?? '—'} (${Utils.formatDate(ph.begindate, 'HH:mm')}-${Utils.formatDate(ph.enddate, 'HH:mm')})`
            );
    }

    private buildGroups(dayReport: Dayreport | null): PrintFieldGroup[] {
        const data = dayReport?.form_data ?? {};
        const hasMixRegistrationMaterial = dayReport?.article_dayreports?.some(adr => adr.material_id === 85) ?? false;

        return Object.entries(baseDayReportFields)
            .filter(([groupKey]) => groupKey !== 'registratie_menging' || hasMixRegistrationMaterial)
            .map(([groupKey, group]) => {
                const fields = Object.entries(group.fields).sort(
                    ([, a], [, b]) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
                );
                const fields1 = fields.filter(([key]) => key.endsWith('_1'));
                const paired =
                    fields1.length > 0 &&
                    fields1.length * 2 === fields.length &&
                    fields1.every(([key]) => group.fields[key.slice(0, -2) + '_2']);

                const rows: PrintFieldRow[] = paired
                    ? fields1.map(([key, field]) => {
                          const key2 = key.slice(0, -2) + '_2';
                          return {
                              label: field.name,
                              value1: this.formatFieldValue(field, data[key]),
                              value2: this.formatFieldValue(group.fields[key2], data[key2]),
                          };
                      })
                    : fields.map(([key, field]) => ({
                          label: field.name,
                          value1: this.formatFieldValue(field, data[key]),
                      }));

                return { key: groupKey, label: group.label, paired, rows };
            });
    }

    private formatFieldValue(field: EntityField, value: any): string {
        if (value === null || value === undefined || value === '') {
            return '';
        }

        switch (field.type) {
            case FieldType.Radio:
            case FieldType.Select:
                return field.options?.find(option => option.key === value)?.name ?? `${value}`;
            case FieldType.BaseDateTime:
                return Utils.formatDate(value, 'HH:mm');
            case FieldType.Checkbox:
                return value ? 'Ja' : 'Nee';
            case FieldType.Number:
                return Number(value).toLocaleString('nl-NL', { maximumFractionDigits: 2 });
            default:
                return `${value}`;
        }
    }

    private buildProduction(
        dayReport: Dayreport | null,
        materials: Material[],
        materialTypes: MaterialType[],
        rawMaterials: RawMaterial[]
    ): void {
        const products = new Map<string, PrintProductRow>();

        this.specificationRows = (dayReport?.article_dayreports ?? []).map((adr: ArticleDayreport) => {
            const material = materials.find(m => m.id === adr.material_id);

            if (!adr.article_crm_id && adr.article_id) {
                (adr.raw_materials_ids ?? []).forEach(rawMaterialId => {
                    const rawMaterial = rawMaterials.find(rm => rm.id === rawMaterialId);
                    const type = materialTypes.find(t => t.id === rawMaterial?.material_type_id);
                    const consumption =
                        material?.raw_materials?.find(rm => rm.id === rawMaterialId)?.pivot?.consumption ??
                        rawMaterial?.consumption;

                    if (type?.hidden || !consumption) {
                        return;
                    }

                    const m2 = (adr.article?.factor ?? 0) * adr.amount;
                    const key = `${rawMaterialId}-${consumption}`;
                    const product = products.get(key) ?? {
                        key,
                        name: rawMaterial?.name ?? '',
                        color: [rawMaterial?.color?.name, rawMaterial?.color?.ral].filter(Boolean).join(' '),
                        supplier: rawMaterial?.supplier?.name,
                        certificate: rawMaterial?.certificate_number,
                        consumption,
                        consumptionUnit: rawMaterial?.consumption_type?.name,
                        m2: 0,
                        kg: 0,
                    };

                    product.m2 += m2;
                    product.kg += consumption * m2;
                    products.set(key, product);
                });
            }

            return adr.article_crm_id
                ? {
                      id: adr.id,
                      location: adr.location ?? '',
                      amount: adr.amount,
                      unit: adr.article_crm?.unit,
                      marking: `Bestekpost: ${adr.article_crm?.description ?? adr.article_crm?.crm_article_id ?? ''}`,
                      m2: null,
                      material: null,
                  }
                : {
                      id: adr.id,
                      location: adr.location ?? '',
                      amount: adr.amount,
                      unit: adr.article?.article_unit_type?.name,
                      marking: `${adr.article?.group ?? ''}; ${adr.article?.name ?? ''}`,
                      m2: adr.article?.factor !== undefined ? adr.amount * adr.article.factor : null,
                      material: material ?? null,
                  };
        });
        this.specificationRows.sort((a, b) => a.location.localeCompare(b.location));
        this.specificationTotalM2 = this.specificationRows.reduce((total, row) => total + (row.m2 ?? 0), 0);

        this.productRows = Array.from(products.values());
        this.productTotalM2 = this.productRows.reduce((total, row) => total + row.m2, 0);
        this.productTotalKg = this.productRows.reduce((total, row) => total + row.kg, 0);
    }
}
